import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchContractDeposits , deleteContract, ContractDeposit, ContractDepositFilter} from './ContractApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSyncAlt, faArrowLeft,faPlus } from '@fortawesome/free-solid-svg-icons';
import { CONTRACT_STATUSES, SECURITY_DEPOSIT_STATUSES } from '../../constants/contractConstants';
import { PROJECT_CONSTANTS } from '../../constants/projectConstants';
import { Contact } from 'lucide-react';
import {EntityType,UploadContexts} from '../../constants/globalConstants'
import { faEye, faEdit, faTrash, faBuildingColumns, faFileUpload , faComments} from "@fortawesome/free-solid-svg-icons";
import ContractActions from './ContractActions';
 


const ContractDepositList: React.FC = () => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState<ContractDepositFilter>({
    depositType: '',
    accountHolderName: '',
    bankAccountNo: '',
    depositStatuses: [],
    bidId: '',
    contractName: '',
    referenceNo: '',
    description: '',
    statuses: [],
    contractType: '',
    licenseHolderName: '',
    pagination: { page: 0, size: 10 },
  });

  const [data, setData] = useState<ContractDeposit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

 

  const handleCreateProject = (contract: ContractDeposit) => {
    //Tenant and user details
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const tenantId = user?.tenantId || '';
    const tenantName = user?.tenantName || '';
    const userId = user?.id || '';

    navigate('/projects/create', {
      state: {
        
        tenantId: tenantId,
        tenantName: tenantName,
        contractId: contract.id,
        contractBidId: contract.bidId,
        createdBy: userId,
        updatedBy: userId,
        projectName: contract.contractName,
        description: contract.description || '',
        projectType: contract.contractType || '',
        status: PROJECT_CONSTANTS.INITIATED,
        contractBidNo: contract.referenceNo,
        projectBidValue: contract.tenderBidValue,
        projectActualValue: contract.tenderActualValue,
        projectBidPercentage: contract.tenderBidPercentage,
        licenseHolderName: contract.licenseHolderName,
        licenseNo: '',
        givenStartDate: contract.givenStartDate|| '',
        givenEndDate: contract.givenEndDate || '',
        actualStartDate: contract.actualStartDate || '',
        actualEndDate: contract.actualEndDate || '',
        extensionCount: 0,
  
        terminationReason: '',
        comment: 'Project created from contract deposit screen',
      }
    });
  };
 
  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this contract?')) {
        console.log('Deleting contract with ID:', id);
      await deleteContract(id);
      handleSearch();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      pagination: { ...prev.pagination, page: 0 },
    }));
  };

  const handleMultiSelectChange = (name: string, selectedOptions: string[]) => {
    setFilters((prev) => ({
      ...prev,
      [name]: selectedOptions,
      pagination: { ...prev.pagination, page: 0 },
    }));
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const size = parseInt(e.target.value, 10) || 1;
    setFilters((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, size, page: 0 },
    }));
  };

  const changePage = (newPage: number) => {
    setFilters((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, page: newPage },
    }));
  };

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchContractDeposits(filters);
      setData(response?.content ?? []);
      setTotalElements(response?.totalElements ?? 0);
      setTotalPages(response?.totalPages ?? 1);
    } catch (err) {
      setError('Failed to fetch contract deposit data.');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters({
      depositType: '',
      accountHolderName: '',
      bankAccountNo: '',
      depositStatuses: [],
      bidId: '',
      contractName: '',
      referenceNo: '',
      description: '',
      statuses: [],
      contractType: '',
      licenseHolderName: '',
      pagination: { page: 0, size: 10 },
    });
  };

  useEffect(() => {
    handleSearch();
  }, [filters.pagination.page, filters.pagination.size]);

  return (
    <div className="p-6 bg-white rounded shadow">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center text-sm text-blue-600 hover:underline"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
        Back
      </button>

      <h1 className="text-xl font-bold mb-4">Contract Deposit Search</h1>

      {/* Filter Section */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 bg-gray-50 p-4 rounded mb-4 border">

        {/* --- Contract Filters --- */}
        <div className="md:col-span-5 border-b pb-2 mb-2 font-semibold text-gray-700">
          Contract Filters
        </div>

        {['bidId', 'contractName', 'referenceNo', 'licenseHolderName', 'contractType', 'description'].map(
          (field) => (
            <div key={field}>
              <label className="text-sm font-medium block mb-1 capitalize">
                {field.replace(/([A-Z])/g, ' $1')}
              </label>
              <input
                type="text"
                name={field}
                placeholder={`Enter ${field}`}
                value={(filters as any)[field]}
                onChange={handleInputChange}
                className="border px-3 py-2 rounded w-full"
              />
            </div>
          )
        )}

        <div>
          <label className="text-sm font-medium block mb-1">Contract Statuses</label>
          <select
            multiple
            value={filters.statuses}
            onChange={(e) =>
              handleMultiSelectChange(
                'statuses',
                Array.from(e.target.selectedOptions, (option) => option.value)
              )
            }
            className="border px-3 py-2 rounded w-full"
          >
            {CONTRACT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        {/* --- Security Deposit Filters --- */}
        <div className="md:col-span-5 border-b pt-4 pb-2 mb-2 font-semibold text-gray-700">
          Security Deposit Filters
        </div>

        {['depositType', 'accountHolderName', 'bankAccountNo'].map((field) => (
          <div key={field}>
            <label className="text-sm font-medium block mb-1 capitalize">
              {field.replace(/([A-Z])/g, ' $1')}
            </label>
            <input
              type="text"
              name={field}
              placeholder={`Enter ${field}`}
              value={(filters as any)[field]}
              onChange={handleInputChange}
              className="border px-3 py-2 rounded w-full"
            />
          </div>
        ))}

        <div>
          <label className="text-sm font-medium block mb-1">Deposit Statuses</label>
          <select
            multiple
            value={filters.depositStatuses}
            onChange={(e) =>
              handleMultiSelectChange(
                'depositStatuses',
                Array.from(e.target.selectedOptions, (option) => option.value)
              )
            }
            className="border px-3 py-2 rounded w-full"
          >
            {SECURITY_DEPOSIT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        {/* Page Size */}
        <div>
          <label className="text-sm font-medium block mb-1">Page Size</label>
          <input
            type="number"
            min={1}
            value={filters.pagination.size}
            onChange={handlePageSizeChange}
            className="border px-3 py-2 rounded w-full"
          />
        </div>

        {/* Search / Reset */}
        <div className="md:col-span-5 flex items-end gap-2 mt-2">
          <button
            onClick={handleSearch}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center"
          >
            <FontAwesomeIcon icon={faSearch} className="mr-2" />
            Apply Filters
          </button>
          <button
            onClick={handleReset}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center">Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 border">Bid ID</th>
                <th className="p-2 border">Contract Name</th>
                <th className="p-2 border">Reference No</th>
                <th className="p-2 border">Status</th>
                <th className="p-2 border">Paper Cost</th>
                <th className="p-2 border">Deposit</th>
                <th className="p-2 border">Total</th>
                <th className="p-2 border">License Holder</th>
                <th className="p-2 border">Submitted Date</th>
                <th className="p-2 border">Contract Type</th>
                <th className="p-2 border">Tender Actual Value</th>
                <th className="p-2 border">Tender Bid Value</th>
                <th className="p-2 border">Tender Bid Percentage</th>
                <th className="p-2 border">Action</th>
                <th className="p-2 border">Project Creation</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="p-2 border">{item.bidId}</td>
                  <td className="p-2 border">{item.contractName}</td>
                  <td className="p-2 border">{item.referenceNo}</td>
                  <td className="p-2 border">{item.status}</td>
                  <td className="p-2 border">₹{item.paperCost}</td>
                  <td className="p-2 border">₹{item.totalDeposit}</td>
                  <td className="p-2 border font-semibold">₹{item.totalAmount}</td>
                  <td className="p-2 border">{item.licenseHolderName}</td>
                  <td className="p-2 border">
                    {item.submittedPaymentDate
                      ? new Date(item.submittedPaymentDate).toLocaleDateString()
                      : '-'}
                  </td>
                  <td className="p-2 border">{item.contractType}</td>
                  <td className="p-2 border">{item.tenderActualValue}</td>
                  <td className="p-2 border">{item.tenderBidValue}</td>
                  <td className="p-2 border">{item.tenderBidPercentage}</td>
                  <td className="p-2 border space-y-2">
                    <ContractActions contract={item} onDelete={handleDelete} />
                  </td>
                  <td className="px-4 py-2 border text-center">
                  {item.status === 'AWARDED' && (
                    <button
                      onClick={() => handleCreateProject(item)}
                      disabled={item.isProjectCreated}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-white text-sm font-medium ${
                        item.isProjectCreated ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Create Project
                    </button>
                  )}
              </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex justify-between mt-4 items-center">
            <span>
              Showing page {filters.pagination.page + 1} of {totalPages} — {totalElements} records
            </span>
            <div className="flex gap-2">
              <button
                disabled={filters.pagination.page === 0}
                onClick={() => changePage(filters.pagination.page - 1)}
                className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
              >
                Prev
              </button>
              <button
                disabled={filters.pagination.page >= totalPages - 1}
                onClick={() => changePage(filters.pagination.page + 1)}
                className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractDepositList;